import { useEffect } from 'react';

/**
 * helpContextStore — a tiny mutable store of "what the user is doing right now"
 * signals, read by HelpChatPanel.buildAppState() when sending a help message.
 *
 * Components push narrow facts (selected count, viewer open, scope size, etc.)
 * via useHelpContextSignal(). The store is intentionally not React state — it's
 * a plain object — because consumers (the help panel) read it only on demand
 * (per message send), not on every render.
 *
 * STRICT RULE — only push UX state here. Never push case content, document IDs,
 * filenames, search query strings, or specific filter values. The store's
 * contents end up in help-chat transcripts, which are themselves potentially
 * discoverable in legal proceedings (decision #7 in AGENTIC_HELP_PROPOSAL.md).
 * Counts, booleans, and enum-style category names only.
 */
const _signals = {};

export function setHelpContextSignal(key, value) {
  if (value === undefined || value === null) {
    delete _signals[key];
  } else {
    _signals[key] = value;
  }
}

export function getHelpContextSignals() {
  return { ..._signals };
}

/**
 * Push `value` under `key` for as long as the calling component is mounted.
 * Cleared on unmount or when `value` changes.
 */
export default function useHelpContextSignal(key, value) {
  useEffect(() => {
    setHelpContextSignal(key, value);
    return () => setHelpContextSignal(key, undefined);
  }, [key, value]);
}
